import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useCurrencyConverter } from "@/hooks/useCurrencyConverter";
type Stats = {
  totalUsers: number;
  todayTransactions: number;
  totalBalance: number;
  currencyBalances: {
    currency: string;
    balance: number;
    symbol: string;
  }[];
  systemUptime: number;
};
interface ExchangeRate {
  id: string;
  base_currency: string;
  quote_currency: string;
  rate: number;
  is_active: boolean;
}
export default function DirectAdminDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    todayTransactions: 0,
    totalBalance: 0,
    currencyBalances: [],
    systemUptime: 99.9
  });
  const [loading, setLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('THB');
  const [currencies, setCurrencies] = useState<{
    code: string;
    symbol: string;
  }[]>([]);
  const {
    formatCurrency: convertCurrency
  } = useCurrencyConverter({
    baseCurrency: 'THB',
    targetCurrency: selectedCurrency,
    exchangeRates
  });
  useEffect(() => {
    Promise.all([fetchRealData(), loadExchangeRates(), loadCurrencies()]);
  }, []);
  const loadExchangeRates = async () => {
    try {
      const {
        data
      } = await supabase.from('exchange_rates').select('*').eq('is_active', true);
      setExchangeRates(data || []);
    } catch (error) {
      console.error('Error loading exchange rates:', error);
    }
  };
  const loadCurrencies = async () => {
    try {
      const {
        data
      } = await supabase.from('currencies').select('code, symbol').eq('is_active', true).order('sort_order');
      setCurrencies(data || []);
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  };
  const fetchRealData = async () => {
    try {
      // Get total users
      const {
        count: totalUsers
      } = await supabase.from("users").select("*", {
        count: "exact",
        head: true
      });

      // Get today's transactions
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      const {
        data: todayTransactions
      } = await supabase.from("transactions").select("amount").gte("created_at", startOfDay.toISOString()).lt("created_at", endOfDay.toISOString());

      // Get balance by currency
      const {
        data: wallets
      } = await supabase.from("user_wallets").select("balance, currency_code");
      const {
        data: currencies
      } = await supabase.from("currencies").select("code, symbol").eq("is_active", true);

      // Group balances by currency
      const currencyBalances = currencies?.map(currency => {
        const balance = wallets?.filter(w => w.currency_code === currency.code)?.reduce((sum, wallet) => sum + (wallet.balance || 0), 0) || 0;
        return {
          currency: currency.code,
          balance,
          symbol: currency.symbol || currency.code
        };
      }).filter(cb => cb.balance > 0) || [];
      const totalBalance = currencyBalances.reduce((sum, cb) => sum + cb.balance, 0);
      setStats({
        totalUsers: totalUsers || 0,
        todayTransactions: todayTransactions?.length || 0,
        totalBalance,
        currencyBalances,
        systemUptime: 99.9
      });
    } catch (error) {
      console.error('Error fetching real data:', error);
    } finally {
      setLoading(false);
    }
  };
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };
  const formatCurrency = (amount: number): string => {
    return `฿${formatNumber(amount)}`;
  };
  const formatCurrencyByCode = (currency: string, amount: number, fallbackSymbol: string): string => {
    try {
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `${fallbackSymbol} ${amount.toLocaleString('th-TH', {
        maximumFractionDigits: 2
      })}`;
    }
  };
  console.log('🎯 DirectAdminDashboard: Component rendering');
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Money+ Wallet Admin Portal</h1>
      </div>
      <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-900 dark:text-green-800">🎉 ระบบแอดมินทำงานได้แล้ว!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-green-900 dark:text-green-700">
              คุณเข้าสู่ระบบแอดมินสำเร็จแล้ว! ตอนนี้คุณสามารถจัดการระบบได้เต็มรูปแบบ
            </p>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => window.location.href = '/admin/users'}>
                จัดการผู้ใช้
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/admin/transactions'} className="text-black dark:text-white">
                ดูธุรกรรม
              </Button>
              <Button variant="secondary" onClick={() => window.location.href = '/admin/reports'} className="text-slate-950">
                รายงาน
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-black dark:text-white">ผู้ใช้ทั้งหมด</CardTitle>
              <span className="text-2xl">👥</span>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black dark:text-white">
                {loading ? "..." : formatNumber(stats.totalUsers)}
              </div>
              <p className="text-xs text-black/70 dark:text-white/70">
                ผู้ใช้ในระบบ
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-black dark:text-white">ธุรกรรมวันนี้</CardTitle>
              <span className="text-2xl">💰</span>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black dark:text-white">
                {loading ? "..." : formatNumber(stats.todayTransactions)}
              </div>
              <p className="text-xs text-black/70 dark:text-white/70">
                รายการวันนี้
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-black dark:text-white">ยอดเงินรวม</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                  <SelectTrigger className="w-20 h-8 text-xs bg-white dark:bg-gray-800 text-black dark:text-white border-gray-300 dark:border-gray-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 z-50">
                    {currencies.map(currency => <SelectItem key={currency.code} value={currency.code} className="text-black dark:text-white">
                        {currency.code}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-2xl">💵</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black dark:text-white">
                {loading ? "..." : convertCurrency(stats.totalBalance, selectedCurrency)}
              </div>
              <p className="text-xs text-black/70 dark:text-white/70">
                ยอดรวมในระบบ ({selectedCurrency})
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-black dark:text-white">ระบบออนไลน์</CardTitle>
              <span className="text-2xl">🟢</span>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black dark:text-white">{stats.systemUptime}%</div>
              <p className="text-xs text-black/70 dark:text-white/70">
                Uptime
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Currency Breakdown Section */}
        {!loading && stats.currencyBalances.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.currencyBalances.map(cb => <Card key={cb.currency}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-black dark:text-white">{cb.currency}</CardTitle>
                  <span className="text-xl">{cb.symbol}</span>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-black dark:text-white">
                    {formatCurrencyByCode(cb.currency, cb.balance, cb.symbol)}
                  </div>
                  <p className="text-xs text-black/70 dark:text-white/70">
                    ยอดในสกุลเงิน {cb.currency}
                  </p>
                </CardContent>
              </Card>)}
          </div>}

        <Card>
          <CardHeader>
            <CardTitle className="text-black dark:text-white">🎛️ เมนูแอดมินหลัก</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button variant="outline" onClick={() => window.location.href = '/admin/users'} className="h-20 flex flex-col text-black dark:text-white">
                <span className="text-2xl mb-1">👥</span>
                จัดการผู้ใช้
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/admin/transactions'} className="h-20 flex flex-col text-black dark:text-white">
                <span className="text-2xl mb-1">💰</span>
                ธุรกรรม
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/admin/reports'} className="h-20 flex flex-col text-black dark:text-white">
                <span className="text-2xl mb-1">📊</span>
                รายงาน
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/admin/settings'} className="h-20 flex flex-col text-black dark:text-white">
                <span className="text-2xl mb-1">⚙️</span>
                ตั้งค่า
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
}